<script>
import { VueFileAgent } from "@boindil/vue-file-agent-next";
import FancyBoxGallery from "@/components/parts/FancyBoxGallery.vue";
import { useI18n } from 'vue-i18n';
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';

export default {
  name: "OrderAttachments",
  components: { VueFileAgent, FancyBoxGallery },
  props: {
    records: { type: Array, default: () => [] },
    title: { type: String, required: true },
    helpText: { type: String, default: '' },
    uploadUrl: { type: String, required: true },
    uploadHeaders: { type: Object, default: () => ({})},
    isGallery: {type: Boolean, default: false},
    zoneName: {type: String, required: true},
    hideDropzone: {type: Boolean, default: false}
  },
  emits: ['upload', 'delete-request', 'set-active-zone'],
  setup() {
    const {t} = useI18n();
    return {t};
  },
  data() {
    return {
      isOpen: true
    };
  },
  mounted() {
    Fancybox.bind(this.$el, '[data-fancybox="gallery"]', {
      groupAll: true,
      infinite: true,
    });
  },
  unmounted() {
    Fancybox.unbind(this.$el);
  },
  methods: {
    onUpload(event) {
      this.$emit('upload', event);
    },
    onBeforeDelete(fileRecord) {
      this.$emit('delete-request', fileRecord);
    },
    onMouseEnter() {
      this.$emit('set-active-zone', this.zoneName);
    },
    onMouseLeave() {
      this.$emit('set-active-zone', null);
    },
    cancelDelete(fileRecord) {
      this.$refs.fileAgent?.filenameChanged(fileRecord);
    }
  }
}
</script>

<template>
  <div class="col-12">
    <div class="card shadow-sm border-0 section-card">
      <div class="card-header bg-light d-flex align-items-center justify-content-between"
           @click="isOpen = !isOpen" style="cursor: pointer; user-select: none;">
        <span class="fw-semibold d-flex align-items-center gap-2">
          <svg v-if="isGallery" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-primary">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M15 8h.01" />
            <path d="M3 6a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v12a3 3 0 0 1 -3 3h-12a3 3 0 0 1 -3 -3v-12z" />
            <path d="M3 16l5 -5c.928 -.893 2.072 -.893 3 0l5 5" />
            <path d="M14 14l1 -1c.928 -.893 2.072 -.893 3 0l3 3" />
          </svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-primary">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M15 7l-6.5 6.5a1.5 1.5 0 0 0 3 3l6.5 -6.5a3 3 0 0 0 -6 -6l-6.5 6.5a4.5 4.5 0 0 0 9 9l6.5 -6.5" />
          </svg>
          {{ title }}
          <span v-if="records.length > 0" class="badge bg-secondary rounded-pill">{{ records.length }}</span>
        </span>
        <svg :class="{'collapse-arrow-rotated': !isOpen}" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-muted" style="transition: transform 0.2s ease;">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M6 9l6 6l6 -6" />
        </svg>
      </div>
      <div :class="{ 'collapse': true, 'show': isOpen }">
        <div class="card-body">
          <FancyBoxGallery v-if="isGallery && records.length > 0">
            <div class="row g-3">
              <div v-for="image in records" :key="image.id" class="col-6 col-md-3">
                <a data-fancybox="gallery" :href="image.url"
                   class="photo-item zoom-hover-effect position-relative d-block overflow-hidden rounded shadow-sm">
                  <img :src="image.type && image.type.startsWith('video/') ? '/video-cover.jpg' : image.url"
                       :alt="image.name"
                       class="w-100 h-100"
                       style="object-fit: cover; aspect-ratio: 1;"/>
                  <div class="hover-overlay d-flex align-items-center justify-content-center position-absolute top-0 start-0 w-100 h-100 rounded" style="pointer-events: none;">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      <line x1="11" y1="8" x2="11" y2="14"></line>
                      <line x1="8" y1="11" x2="14" y2="11"></line>
                    </svg>
                  </div>
                </a>
              </div>
            </div>
          </FancyBoxGallery>

          <div class="row" :class="{ 'mt-3': isGallery && records.length > 0 }" v-if="!hideDropzone" @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
            <div class="col">
              <VueFileAgent
                  ref="fileAgent"
                  :auto="true"
                  :uploadUrl="uploadUrl"
                  :rawModelValue="records"
                  :deletable="true"
                  :multiple="true"
                  :meta="true"
                  :linkable="!isGallery"
                  :uploadWithCredentials="true"
                  :helpText="helpText || t('placeholders.selectFiles')"
                  :theme="'list'"
                  :uploadHeaders="uploadHeaders"
                  @upload="onUpload"
                  @beforedelete="onBeforeDelete"
              />
            </div>
          </div>

          <p v-if="hideDropzone && (!isGallery || records.length === 0)" class="text-muted small mb-0">
            {{ t('table.placeholders.empty_heading', 'Дані ще не додані.') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.section-card {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;

  .card-header {
    border-bottom: 1px solid #e9ecef;
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
    font-size: 0.95rem;
  }
}

.collapse-arrow-rotated {
  transform: rotate(-90deg);
}

.zoom-hover-effect {
  cursor: zoom-in;

  img {
    transition: transform 0.3s ease;
  }

  .hover-overlay {
    background: rgba(0, 0, 0, 0.4);
    opacity: 0;
    transition: opacity 0.3s ease;

    svg {
      transform: scale(0.5);
      transition: transform 0.3s ease;
    }
  }

  &:hover {
    img {
      transform: scale(1.05);
    }

    .hover-overlay {
      opacity: 1;

      svg {
        transform: scale(1);
      }
    }
  }
}

@media (hover: none) {
  .zoom-hover-effect .hover-overlay {
    opacity: 1;
    background: transparent;
    justify-content: flex-end !important;
    align-items: flex-start !important;
    padding: 6px;

    svg {
      transform: scale(1);
      width: 30px;
      height: 30px;
      padding: 5px;
      background: rgba(0, 0, 0, 0.55);
      border-radius: 50%;
    }
  }
}
</style>